import { captureReadingContact } from "@/lib/api/chat";
import { trackEvent } from "@/lib/analytics-events";
import { rememberCheckoutPrefillFromContact } from "@/lib/checkout-prefill";
import { CAPTURE_POINTS } from "@/lib/contact-capture-points";
import { normalizePhoneE164 } from "@/lib/phone-e164";
import { KUNDALI_PHONE_CAPTURE_POLICY } from "@/lib/phone-capture-policy";

/**
 * Persist the chat-entry form's phone (required) + email (optional) via the
 * EXISTING consent-correct backend (captureReadingContact → POST
 * /readings/{id}/contact). The backend already accepts phone-only, so no
 * backend change is needed. The phone becomes the reading/account identity;
 * marketing permission remains a separate explicit opt-in (ADR-0043).
 *
 * Called AFTER the kundali/reading is created (so a real reading_id exists) so
 * the contact lands BEFORE message 1 on the chat surface. This lets us later
 * revert the chat soft-wall without losing contacts.
 *
 * Identity is a required durability boundary (ADR-0043), so the call retries
 * twice and then raises a user-retryable error instead of silently navigating
 * away with a chart that cannot be recognized later. Checkout prefill is saved
 * locally before the backend call. No-op only for an absent reading id or a
 * phone shape the already-completed form validation could not produce.
 */
export async function persistFormContact(
  readingId: string | undefined | null,
  phone: string,
  email: string
): Promise<void> {
  const normalizedPhone = readingId ? normalizePhoneE164(phone) : null;
  if (!readingId || !normalizedPhone) return;
  const trimmedEmail = email.trim();
  rememberCheckoutPrefillFromContact({
    phone: normalizedPhone,
    email: trimmedEmail || undefined,
  });
  const body = {
    phone: normalizedPhone,
    email: trimmedEmail || undefined,
    consent_marketing: KUNDALI_PHONE_CAPTURE_POLICY.consentMarketing,
  };
  let lastError: unknown;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await captureReadingContact(readingId, body);
      trackEvent("contact_captured", {
        reading_id: readingId,
        type: trimmedEmail ? "phone_email" : "phone",
        placement: "kundali-form-contact",
        capture_point: CAPTURE_POINTS.KUNDALI_FORM_CONTACT,
        consent_marketing: KUNDALI_PHONE_CAPTURE_POLICY.consentMarketing,
        attempt,
      });
      return;
    } catch (error) {
      lastError = error;
    }
  }
  trackEvent("contact_capture_failed", {
    reading_id: readingId,
    placement: "kundali-form-contact",
    capture_point: CAPTURE_POINTS.KUNDALI_FORM_CONTACT,
    attempts: 3,
  });
  throw new FormContactPersistenceError({ cause: lastError });
}

export class FormContactPersistenceError extends Error {
  constructor(options?: ErrorOptions) {
    super(
      "Your kundali is ready, but we could not save your mobile number. Please retry.",
      options
    );
    this.name = "FormContactPersistenceError";
  }
}
